/**
 * Affiliate Agent (Daisycon) — admin tab.
 *
 * Read-only fetch + CSV fallback + approval queue.
 * Records never become visible to customers without explicit admin approval.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, AlertTriangle, CheckCircle2, XCircle, Loader2,
  Archive, RefreshCw, Upload, FileText, ExternalLink, Eye,
} from 'lucide-react';

const API = '/api';

const STATUS_BADGE = {
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
  archived: { label: 'Archived', cls: 'bg-muted text-muted-foreground' },
};

const SAFETY_BADGE = {
  safe_affiliate: { label: 'Safe affiliate', cls: 'bg-green-50 text-green-700 border-green-200' },
  needs_claim_review: { label: 'Needs claim review', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  installer_required: { label: 'Installer required', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  regulated_or_sensitive: { label: 'Regulated/sensitive', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  not_relevant: { label: 'Not relevant', cls: 'bg-muted text-muted-foreground border-border' },
  manual_review_required: { label: 'Manual review (PII?)', cls: 'bg-red-50 text-red-700 border-red-200' },
};

function authHeaders(token) {
  return { headers: { Cookie: `access_token=${token}` }, withCredentials: true };
}

function StatusPanel({ status, onRefresh }) {
  if (!status) return null;
  const cfg = status.config || {};
  return (
    <div className="bg-white rounded-xl border border-border p-4 shadow-sm space-y-3" data-testid="affiliate-agent-status-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-primary" />
          Daisycon configuratie
        </h3>
        <button onClick={onRefresh} className="text-xs flex items-center gap-1 text-muted-foreground hover:text-primary" data-testid="affiliate-agent-refresh-status">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="text-xs">
        <span className="text-muted-foreground">Status:</span>{' '}
        {cfg.configured
          ? <span className="text-green-700 font-semibold">configured · API ready</span>
          : <span className="text-amber-700 font-semibold">not_configured · CSV fallback actief</span>}
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        {(cfg.env_vars || []).map(v => (
          <div key={v.name} className="text-[11px] flex items-center justify-between bg-muted/30 rounded px-2 py-1">
            <span className="font-mono">{v.name}</span>
            <span className={v.status === 'missing' ? 'text-amber-700' : 'text-green-700'}>
              {v.status === 'missing' ? 'missing' : (v.masked_value || 'configured')}
            </span>
          </div>
        ))}
      </div>

      {!cfg.configured && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 flex items-start gap-2">
          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>
            Vul DAISYCON_OAUTH_CLIENT_ID / SECRET / REFRESH_TOKEN + PUBLISHER_ID in backend/.env in,
            of gebruik CSV import hieronder. Records komen sowieso als <code className="bg-amber-100 px-1 rounded">pending</code> binnen.
          </span>
        </div>
      )}

      {status.counters && (
        <div className="grid grid-cols-4 gap-2 pt-2 border-t border-border text-xs">
          {Object.entries(status.counters.campaigns || {}).map(([k, v]) => (
            <div key={k} className="text-center">
              <div className="text-xs text-muted-foreground capitalize">{k}</div>
              <div className="text-lg font-semibold">{v}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ImportPanel({ token, onDone }) {
  const [csvText, setCsvText] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [apiBusy, setApiBusy] = useState(false);

  const submitCsv = async () => {
    if (!csvText.trim()) return toast.error('Plak eerst CSV inhoud');
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/affiliate-agent/import/csv`,
        { csv_text: csvText, dry_run: dryRun },
        authHeaders(token),
      );
      setResult(data);
      toast.success(dryRun
        ? `Dry-run: ${data.campaigns_found} records gevonden`
        : `Import: ${data.campaigns_created} created, ${data.campaigns_updated} updated, ${data.campaigns_protected || 0} protected`);
      if (!dryRun) onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    }
    setBusy(false);
  };

  const tryApi = async () => {
    setApiBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/affiliate-agent/import/api/programs?dry_run=${dryRun}`,
        {},
        authHeaders(token),
      );
      setResult(data);
      if (data.status === 'not_configured') {
        toast.warning('Daisycon API niet geconfigureerd — gebruik CSV import');
      } else {
        toast.success(`API run: ${data.campaigns_found ?? 0} programs gevonden`);
        if (!dryRun) onDone && onDone();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'API import failed');
    }
    setApiBusy(false);
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/affiliate-agent/import/csv-template`, authHeaders(token));
      const blob = new Blob([data.example_csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'daisycon-affiliate-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Template download failed'); }
  };

  return (
    <div className="bg-white rounded-xl border border-blue-200 p-4 shadow-sm space-y-3" data-testid="affiliate-agent-import-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Upload className="w-4 h-4 text-blue-600" />
          Import campagnes (Daisycon)
        </h3>
        <label className="text-[11px] flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={e => setDryRun(e.target.checked)}
            data-testid="affiliate-agent-dryrun-toggle"
          />
          <span>Dry-run (geen schrijfacties)</span>
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={tryApi}
          disabled={apiBusy}
          className="px-3 py-1.5 text-xs bg-primary text-white rounded-md hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
          data-testid="affiliate-agent-import-api-btn"
        >
          {apiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Daisycon API ophalen
        </button>
        <button
          onClick={downloadTemplate}
          className="px-3 py-1.5 text-xs bg-muted text-foreground rounded-md hover:bg-muted/80 flex items-center gap-1"
          data-testid="affiliate-agent-template-btn"
        >
          <FileText className="w-3 h-3" /> Template CSV
        </button>
      </div>

      <textarea
        value={csvText}
        onChange={e => setCsvText(e.target.value)}
        rows={5}
        placeholder="Plak CSV inhoud (header row verplicht). Verplicht: advertiser_name of campaign_name. Optioneel: category, tracking_url, commission_type, commission_value, country, language, ..."
        className="w-full text-xs border border-border rounded p-2 font-mono"
        data-testid="affiliate-agent-csv-input"
      />

      <div className="flex flex-wrap gap-2">
        <button
          onClick={submitCsv}
          disabled={busy}
          className="px-3 py-1.5 text-xs bg-primary text-white rounded-md hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
          data-testid="affiliate-agent-import-csv-btn"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {dryRun ? 'Run dry-run' : 'Import als pending'}
        </button>
      </div>

      {result && (
        <div className="bg-muted/30 rounded p-2 text-[11px] space-y-1" data-testid="affiliate-agent-import-result">
          <div>
            <strong>Status:</strong> {result.status} · found {result.campaigns_found ?? 0} ·
            created {result.campaigns_created ?? 0} · updated {result.campaigns_updated ?? 0}
            {result.campaigns_protected !== undefined && <> · protected {result.campaigns_protected}</>}
          </div>
          {(result.errors || []).length > 0 && (
            <details>
              <summary className="cursor-pointer text-amber-700">Errors ({result.errors.length})</summary>
              <ul className="mt-1 space-y-0.5 max-h-32 overflow-auto">
                {result.errors.slice(0, 50).map((e, i) => (
                  <li key={i} className="text-red-700">row {e.row_number}: {e.reason}</li>
                ))}
              </ul>
            </details>
          )}
          {(result.samples || []).length > 0 && (
            <details>
              <summary className="cursor-pointer text-blue-700">Samples ({result.samples.length})</summary>
              <pre className="mt-1 text-[10px] bg-white rounded p-2 overflow-auto max-h-48">
                {JSON.stringify(result.samples, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function CampaignRow({ camp, onApprove, onReject, onArchive }) {
  const [busy, setBusy] = useState(false);
  const sb = STATUS_BADGE[camp.admin_status] || STATUS_BADGE.pending;
  const safety = SAFETY_BADGE[camp.safety_label] || SAFETY_BADGE.not_relevant;

  const act = async (fn) => {
    setBusy(true);
    try { await fn(camp.id); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-lg p-3 space-y-2" data-testid={`affiliate-campaign-${camp.id}`}>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{camp.advertiser_name}</span>
            <span className="text-xs text-muted-foreground">{camp.campaign_name}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sb.cls}`}>{sb.label}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${safety.cls}`}>{safety.label}</span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            relevance <strong>{camp.relevance_score}</strong> · category {camp.category || '—'}
            {(camp.powersure_categories || []).length > 0 && <> · ps: {camp.powersure_categories.join(', ')}</>}
            {camp.commission_type && <> · {camp.commission_type} {camp.commission_value ?? ''}</>}
            <> · {camp.country || 'NL'}/{camp.language || 'nl'}</>
          </div>
          {camp.terms_summary && (
            <p className="text-[11px] text-muted-foreground italic mt-1 line-clamp-2">{camp.terms_summary}</p>
          )}
          {(camp.url_pii_findings || []).length > 0 && (
            <p className="text-[11px] text-red-700 bg-red-50 rounded p-1 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              PII patterns gedetecteerd in URL — handmatige review vereist
            </p>
          )}
        </div>
        {camp.tracking_url_template && (
          <a
            href={camp.tracking_url_template}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-primary hover:underline flex items-center gap-0.5"
          >
            URL <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {camp.admin_status === 'pending' && (
        <div className="flex gap-2 flex-wrap pt-1 border-t border-border">
          <button
            onClick={() => act(onApprove)}
            disabled={busy}
            className="px-2.5 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
            data-testid={`affiliate-approve-${camp.id}`}
          >
            <CheckCircle2 className="w-3 h-3" /> Approve
          </button>
          <button
            onClick={() => act(onReject)}
            disabled={busy}
            className="px-2.5 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
            data-testid={`affiliate-reject-${camp.id}`}
          >
            <XCircle className="w-3 h-3" /> Reject
          </button>
          <button
            onClick={() => act(onArchive)}
            disabled={busy}
            className="px-2.5 py-1 text-xs bg-muted text-foreground rounded hover:bg-muted/80 disabled:opacity-50 flex items-center gap-1"
            data-testid={`affiliate-archive-${camp.id}`}
          >
            <Archive className="w-3 h-3" /> Archive
          </button>
        </div>
      )}
    </div>
  );
}

export default function AffiliateAgentTab({ token }) {
  const [status, setStatus] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [runs, setRuns] = useState([]);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [st, cs, rs] = await Promise.all([
        axios.get(`${API}/admin/affiliate-agent/status`, authHeaders(token)),
        axios.get(`${API}/admin/affiliate-agent/campaigns?status=${filterStatus}&limit=200`, authHeaders(token)),
        axios.get(`${API}/admin/affiliate-agent/import-runs?limit=10`, authHeaders(token)),
      ]);
      setStatus(st.data);
      setCampaigns(cs.data.items || []);
      setRuns(rs.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load affiliate agent data');
    }
    setLoading(false);
  }, [token, filterStatus]);

  useEffect(() => { reload(); }, [reload]);

  const handleApprove = async (id) => {
    try {
      await axios.post(`${API}/admin/affiliate-agent/campaigns/${id}/approve`, {}, authHeaders(token));
      toast.success('Campaign approved');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
  };
  const handleReject = async (id) => {
    try {
      await axios.post(`${API}/admin/affiliate-agent/campaigns/${id}/reject`, { reason: 'Niet relevant of niet veilig' }, authHeaders(token));
      toast.success('Campaign rejected');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reject failed'); }
  };
  const handleArchive = async (id) => {
    try {
      await axios.post(`${API}/admin/affiliate-agent/campaigns/${id}/archive`, {}, authHeaders(token));
      toast.success('Campaign archived');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Archive failed'); }
  };

  return (
    <div className="space-y-4" data-testid="affiliate-agent-tab">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-900">
          <strong>Read-only + approval-first.</strong> Daisycon data wordt opgehaald als <em>pending</em>.
          Records worden nooit zichtbaar voor klanten zonder expliciete approval. Geen klant-PII gaat naar Daisycon.
        </p>
      </div>

      <StatusPanel status={status} onRefresh={reload} />
      <ImportPanel token={token} onDone={reload} />

      <div className="bg-white rounded-xl border border-border p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Eye className="w-4 h-4 text-primary" />
            Review queue
          </h3>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="text-xs border border-border rounded px-2 py-1 bg-white"
            data-testid="affiliate-agent-status-filter"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Laden…
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            Geen records in status &quot;{filterStatus}&quot;. Importeer eerst via API of CSV.
          </div>
        ) : (
          <div className="space-y-2">
            {campaigns.map(c => (
              <CampaignRow
                key={c.id || c.external_campaign_id}
                camp={c}
                onApprove={handleApprove}
                onReject={handleReject}
                onArchive={handleArchive}
              />
            ))}
          </div>
        )}
      </div>

      {runs.length > 0 && (
        <details className="bg-white rounded-xl border border-border p-4 shadow-sm" data-testid="affiliate-agent-runs-log">
          <summary className="text-sm font-semibold cursor-pointer">Import run history (laatste 10)</summary>
          <div className="mt-3 space-y-1 text-xs">
            {runs.map(r => (
              <div key={r.run_id} className="flex items-center justify-between bg-muted/30 rounded px-2 py-1">
                <span>
                  <strong>{r.source}</strong> · {r.started_at?.slice(0, 19)?.replace('T', ' ')} ·
                  status {r.status}
                </span>
                <span>
                  found {r.campaigns_found ?? 0} · created {r.campaigns_created ?? 0} ·
                  updated {r.campaigns_updated ?? 0} · dry_run {String(r.dry_run)}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
